import React, { useState, useContext } from 'react';
import {
  TextField,
  Box,
  Typography,
  ThemeProvider,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from './config';
import { ThemeContext } from './ThemeContext';
import ThemeToggle from './ThemeToggle';
import TermsDialog from './TermsDialog'; // Import the new TermsDialog component
import logo from './assets/logo.png'; // Adjust path as necessary

const Login = ({ setIsAuthenticated }) => {
  const { theme } = useContext(ThemeContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [openCnsDialog, setOpenCnsDialog] = useState(false);
  const [cns, setCns] = useState('');
  const [cnsError, setCnsError] = useState('');
  const [openTermsDialog, setOpenTermsDialog] = useState(false);
  const navigate = useNavigate();
  const [successAlert, setSuccessAlert] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('dados');
    setIsAuthenticated(false);
    navigate('/');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      alert('Por favor, preencha todos os campos.');
      return;
    }
    try {
      const loginResponse = await axios.post(`${config.API_URL}/login`, { email, password });
      const { access_token, dados, firstLogin } = loginResponse.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('dados', dados);
      
      if (firstLogin !== 1) {
        setOpenTermsDialog(true); // Show terms dialog
      } else {
        localStorage.removeItem('imovelFormData');
        setIsAuthenticated(true);
        navigate('/selection');
      }
    } catch (error) {
      console.error('Erro no login ou ao buscar dados:', error.response || error.message);
      alert('Erro: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleOpenCnsDialog = () => {
    setOpenCnsDialog(true);
    setCns('');
    setCnsError('');
  };

  const handleCloseCnsDialog = () => {
    setOpenCnsDialog(false);
    setCns('');
    setCnsError('');
  };

  const handleCnsSubmit = async () => {
    if (!cns) {
      setCnsError('O CNS é obrigatório.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${config.API_URL}/create-user-from-cns`, { cns });
      setSuccessAlert(true);
      setCnsError('');
      setLoading(false);
      setTimeout(() => {
        handleCloseCnsDialog();
        setSuccessAlert(false);
      }, 2000);
    } catch (error) {
      setLoading(false);
      console.error('Erro ao validar CNS:', error.response || error.message);
      const detail = error.response?.data?.detail ?? 'Erro ao validar CNS. Tente novamente.';
      setCnsError(
        <div>
          <p>{detail}.</p>
          <p>Entre em contato pelo WhatsApp: (31) 12345-7891</p>
        </div>
      );
    }
  };

  const handleTermsAccept = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${config.API_URL}/accept-terms`, 
        { email },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            }
          });
      setOpenTermsDialog(false);
      localStorage.removeItem('imovelFormData');
      setIsAuthenticated(true);
      navigate('/selection');
    } catch (error) {
      console.error('Erro ao aceitar os termos:', error.response || error.message);
      alert('Erro ao aceitar os termos. Tente novamente.');
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
        <Box
          sx={{
            width: 60,
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 2,
            gap: 2,
          }}
        >
          <Tooltip title="Login" placement="right">
            <IconButton>
              <LoginIcon />
            </IconButton>
          </Tooltip>
          <ThemeToggle sx={{ mt: 'auto' }} />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: 4,
            gap: 3,
          }}
        >
          <Box
            component="form"
            onSubmit={handleLogin}
            sx={{ maxWidth: 400, width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <img
                src={logo}
                alt="Logo"
                style={{ maxWidth: '250px', height: 'auto', borderRadius: '16px' }}
              />
            </Box>
            <Typography variant="h4" sx={{ color: 'text.primary', mb: 2, textAlign: 'center' }}>
              Login
            </Typography>
            <TextField
              fullWidth
              label="E-mail"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
            <TextField
              fullWidth
              label="Senha"
              variant="outlined"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              variant="contained"
             

 sx={{ mt: 2, borderRadius: '8px', textTransform: 'none' }}
            >
              Entrar
            </Button>
            <Typography
              variant="body1"
              sx={{
                mt: 1,
                textAlign: 'center',
                color: 'success.main',
                cursor: 'pointer',
                '&:hover': {
                  color: 'success.dark',
                  textDecoration: 'underline',
                },
              }}
              onClick={handleOpenCnsDialog}
            >
              Faça seu teste agora
            </Typography>
          </Box>
        </Box>
      </Box>
      <Dialog
        open={openCnsDialog}
        onClose={handleCloseCnsDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2,
            width: '100%',
            maxWidth: 400,
            boxShadow: 10,
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
          },
        }}
      >
        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {successAlert && (
            <Alert severity="success" onClose={() => setSuccessAlert(false)}>
              Um email com senha foi enviado para o email associado.
            </Alert>
          )}
          {cnsError && (
            <Alert severity="error" onClose={() => setCnsError('')}>
              {cnsError}
            </Alert>
          )}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          )}
          <TextField
            autoFocus
            fullWidth
            label="CNS"
            type="number"
            variant="outlined"
            value={cns}
            onChange={(e) => {
              setCns(e.target.value);
              setCnsError('');
              setSuccessAlert(false);
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && cns && /^\d{15}$/.test(cns)) {
                handleCnsSubmit();
              }
            }}
            error={!!cnsError}
            inputProps={{ maxLength: 15 }}
            disabled={loading}
            sx={{
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                display: 'none',
              },
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
            }}
          />
          <Button
            onClick={handleCnsSubmit}
            variant="contained"
            fullWidth
            sx={{
              mt: 1,
              py: 1.5,
              borderRadius: 1,
            }}
          >
            Confirmar
          </Button>
        </DialogContent>
      </Dialog>
      {openCnsDialog && (
        <IconButton
          onClick={handleCloseCnsDialog}
          sx={{
            position: 'fixed',
            top: 16,
            right: 16,
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
            },
            zIndex: 2000,
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
      <TermsDialog
        open={openTermsDialog}
        onAccept={handleTermsAccept}
        onDecline={handleLogout}
        email={email}
      />
    </ThemeProvider>
  );
};

export default Login;